// frontend/src/pages/Completion.js
import React, { useState, useEffect } from 'react';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import {
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    DocumentTextIcon,
    ShareIcon,
    ClipboardIcon
} from '@heroicons/react/24/outline';
import PageHeader from '../components/common/PageHeader';
import AchievementsPanel from '../features/achievements/AchievementsPanel';

const Completion = () => {
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null);
    const [certificate, setCertificate] = useState(null);
    const [transcript, setTranscript] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            await axios.post('/completion/achievements/evaluate').catch(() => null);
            const statusRes = await axios.get('/completion/status');
            const statusData = statusRes.data.data;
            setStatus(statusData);

            const certSummary = statusData?.certificate;
            setCertificate(certSummary || null);

            if (certSummary) {
                try {
                    const transcriptRes = await axios.get('/completion/transcript');
                    setTranscript(transcriptRes.data.data || null);
                } catch {
                    setTranscript(null);
                }
            } else {
                setTranscript(null);
            }
        } catch (error) {
            toast.error('Failed to load completion data');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitForReview = async () => {
        setSubmitting(true);
        try {
            await axios.post('/completion/submit-review');
            toast.success('Submitted for final review!');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to submit');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopyLink = async () => {
        if (certificate?.verification_token) {
            const url = `${window.location.origin}/verify/${certificate.verification_token}`;
            try {
                await navigator.clipboard.writeText(url);
                toast.success('Verification link copied!');
            } catch {
                toast.error('Failed to copy link');
            }
        }
    };

    const handleShareLinkedIn = () => {
        if (certificate?.verification_token) {
            const url = `${window.location.origin}/verify/${certificate.verification_token}`;
            const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
            window.open(shareUrl, '_blank');
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" role="status" aria-live="polite">
                <div className="skeleton h-10 w-64" />
                <div className="skeleton h-40" />
                <span className="sr-only">Loading completion status…</span>
            </div>
        );
    }

    const { requirements, finalReview, isComplete, isReadyForReview, achievements } = status || {};
    const checks = requirements?.checks || {};
    const canSubmit = requirements?.allRequirementsMet && !isReadyForReview && !isComplete;

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Achievements & Completion"
                description="Track milestones, meet the completion gate, and receive your verified digital certificate"
            />

            {/* Achievements */}
            <AchievementsPanel achievements={achievements} />

            {isComplete ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <CheckCircleIcon className="w-16 h-16 mx-auto text-green-600" />
                    <h2 className="text-2xl font-bold text-green-800 mt-2">Congratulations!</h2>
                    <p className="text-green-700 mt-1 text-sm">
                        You have successfully completed the AI Business Transformation Internship.
                    </p>
                    {certificate && (
                        <div className="mt-4">
                            {certificate.career_direction && (
                                <p className="mb-1 text-sm text-green-800">
                                    Career direction: <span className="font-semibold">{certificate.career_direction}</span>
                                </p>
                            )}
                            <p className="text-sm font-semibold text-green-800">
                                Credential ID: {certificate.credential_id}
                            </p>
                            <p className="text-xs text-green-600">
                                Issue Date: {new Date(certificate.issue_date).toLocaleDateString()}
                            </p>
                            <div className="mt-4 flex flex-wrap justify-center gap-3">
                                <button
                                    onClick={() => window.open(`/verify/${certificate.verification_token}`, '_blank')}
                                    className="px-4 py-2 bg-brand-700 text-white rounded-md hover:bg-brand-800 flex items-center space-x-2 text-xs font-medium"
                                >
                                    <DocumentTextIcon className="w-4 h-4" />
                                    <span>View Certificate</span>
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-ink-50 flex items-center space-x-2 text-xs font-medium text-ink-700"
                                >
                                    <ClipboardIcon className="w-4 h-4" />
                                    <span>Copy Link</span>
                                </button>
                                <button
                                    onClick={handleShareLinkedIn}
                                    className="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 flex items-center space-x-2 text-xs font-medium"
                                >
                                    <ShareIcon className="w-4 h-4" />
                                    <span>Share on LinkedIn</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : isReadyForReview ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <ClockIcon className="w-16 h-16 mx-auto text-yellow-600" />
                    <h2 className="text-2xl font-bold text-yellow-800 mt-2">Under Review</h2>
                    <p className="text-yellow-700 mt-1 text-sm">
                        Submitted for program manager final review. Certificate issues only after approval.
                    </p>
                </div>
            ) : (
                <div className="card p-6">
                    <h2 className="text-lg font-semibold text-ink-950 mb-2">Completion gate</h2>
                    <p className="text-sm text-ink-600 mb-4">
                        All requirements must be met before final review and certificate issuance.
                    </p>
                    <div className="space-y-3">
                        <RequirementItem label="Practical hours" met={checks.hours_complete} detail="Approved hours meet required total" />
                        <RequirementItem label="Mandatory learning modules" met={checks.mandatory_modules_complete} detail="Complete assigned mandatory modules" />
                        <RequirementItem label="Practical assessments / evidence" met={checks.practical_assessments_complete} detail="Approved evidence or business assessment" />
                        <RequirementItem label="Final project / pilot" met={checks.final_project_complete} detail="Completed supervised project or pilot" />
                        <RequirementItem label="Mentor evaluation" met={checks.mentor_evaluation_complete} detail="Mentor evaluation submitted or approved" />
                        <RequirementItem label="Talent profile" met={checks.profile_complete} detail="Profile completion 80%+" />
                        <RequirementItem label="Professional reflections" met={checks.reflections_complete} detail="Submitted or published reflections" />
                        <RequirementItem label="Ready for PM approval" met={checks.pm_approval_ready} detail="Core requirements complete  then program manager review" />
                    </div>
                    <div className="mt-6 pt-4 border-t border-border flex justify-between items-center">
                        <div>
                            <p className="text-xs text-ink-500">Overall Progress</p>
                            <p className="text-2xl font-bold text-ink-950">{requirements?.percentage || 0}%</p>
                        </div>
                        <button
                            onClick={handleSubmitForReview}
                            disabled={submitting || !canSubmit}
                            className="px-6 py-2.5 bg-brand-700 text-white text-sm font-medium rounded-md hover:bg-brand-800 disabled:opacity-50"
                        >
                            {submitting ? 'Submitting...' : 'Submit for Final Review'}
                        </button>
                    </div>
                </div>
            )}

            {finalReview && finalReview.status !== 'pending' && (
                <div className={`rounded-lg p-4 border ${
                    finalReview.status === 'passed' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}>
                    <div className="flex items-start space-x-3">
                        {finalReview.status === 'passed' ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-600 mt-0.5" />
                        ) : (
                            <XCircleIcon className="w-5 h-5 text-red-600 mt-0.5" />
                        )}
                        <div>
                            <h3 className={`font-medium text-sm ${
                                finalReview.status === 'passed' ? 'text-green-800' : 'text-red-800'
                            }`}>
                                Final Review: {finalReview.status.toUpperCase()}
                            </h3>
                            {finalReview.comments && (
                                <p className="text-xs text-ink-700 mt-1">Feedback: {finalReview.comments}</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {transcript && (
                <div className="card p-6 space-y-3">
                    <h2 className="text-lg font-semibold text-ink-950">Professional transcript</h2>
                    <p className="text-sm text-ink-600">
                        {transcript.total_hours_approved} approved hours  {transcript.business_assessment_count || 0} assessments  {transcript.process_maps_count || 0} process maps
                    </p>
                    {Array.isArray(transcript.learning_modules_completed) && transcript.learning_modules_completed.length > 0 && (
                        <div>
                            <p className="text-xs font-medium text-ink-700 mb-1">Learning modules completed</p>
                            <ul className="text-xs text-ink-600 list-disc pl-4">
                                {transcript.learning_modules_completed.map((m) => (
                                    <li key={m}>{m}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {Array.isArray(transcript.project_summaries) && transcript.project_summaries.length > 0 && (
                        <div>
                            <p className="text-xs font-medium text-ink-700 mb-1">Projects</p>
                            <ul className="text-xs text-ink-600 space-y-1">
                                {transcript.project_summaries.map((p, i) => (
                                    <li key={i}><span className="font-medium">{p.name}</span>{p.description ? `  ${p.description}` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const RequirementItem = ({ label, met, detail }) => (
    <div className="flex items-start space-x-3 p-3 bg-ink-50 rounded-lg">
        <div className="flex-shrink-0 mt-0.5">
            {met ? (
                <CheckCircleIcon className="w-5 h-5 text-green-600" />
            ) : (
                <XCircleIcon className="w-5 h-5 text-ink-400" />
            )}
        </div>
        <div>
            <p className={`text-sm font-medium ${met ? 'text-ink-950' : 'text-ink-600'}`}>{label}</p>
            <p className="text-xs text-ink-500">{detail}</p>
        </div>
    </div>
);

export default Completion;
